import {
  CheckOperator,
  MemoryCheckValueType,
  MemoryType,
  MemoryValue,
  StepCheckOperatorType,
} from '../../../components/pages/Book/MemoryBank/memoryBankTypes';
import type { Branch } from '../../../dorian-shared/types/branch/Branch';
import {
  BranchStepMemoryActionType,
  BranchStepMemoryRollType,
  StepTypes,
  type BranchStep,
} from '../../../dorian-shared/types/branch/BranchStep';
import { CHARACTER_MEMORY_PREFIX } from '../utils/characterMemoryPrefix';
import { EXPRESSION_VALUE_TO_COMMAND } from '../types/episodeTextImportCommandKeyValues';
import { EpisodeTextImportCommandKey } from '../types/episodeTextImportCommandTypes';

export type ParserDictionaries = {
  characters: Map<number, string>;
  expressions: Map<number, string>;
  variables: Map<number, string>;
  variableTypes: Map<number, string>;
  branchNames: Map<number, string>;
  achievements: Map<number, string>;
  rewards: Map<number, string>;
};

function resolveMemoryValue(value: unknown, variables: Map<number, string>): string | number {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'string' && value.toLowerCase() === 'true') return 'yes';
  if (typeof value === 'string' && value.toLowerCase() === 'false') return 'no';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (typeof value === 'object' && 'type' in (value as Record<string, unknown>)) {
    const obj = value as { type: string; variableId?: number | number[]; variable?: string | string[] };
    if (obj.type === 'variable') {
      if (obj.variableId != null) {
        return variables.get(Number(obj.variableId)) ?? String(obj.variableId);
      }
      if (obj.variable != null) return String(obj.variable);
    }
    if (obj.type === 'variable[]') {
      if (Array.isArray(obj.variableId)) {
        return obj.variableId.map(id => variables.get(Number(id)) ?? String(id)).join(', ');
      }
      if (Array.isArray(obj.variable)) return (obj.variable as string[]).join(', ');
    }
  }
  return String(value);
}

export class BranchTextParser {
  private branches: Branch[];
  private dictionaries: ParserDictionaries;

  constructor(branches: Branch[], dictionaries: ParserDictionaries) {
    this.branches = branches;
    this.dictionaries = dictionaries;
  }

  public toText(): string {
    const lines: string[] = [];

    this.branches.forEach((branch) => {
      lines.push(...this.parseBranch(branch));
    });

    return lines.join('\n');
  }

  private parseBranch(branch: Branch): string[] {
    const lines: string[] = [];
    const bidTag = branch.id != null ? `\t__bid:${branch.id}` : '';

    if (branch.location?.alias) {
      lines.push(`${branch.title}\t\t#${EpisodeTextImportCommandKey.Location} ${branch.location.alias}${bidTag}`);
    } else {
      lines.push(`${branch.title}\t\t${bidTag}`);
    }

    const steps = branch.steps ?? [];
    steps.forEach((step, index) => {
      const isLastStep = index === steps.length - 1;
      lines.push(...this.parseStep(step, branch, isLastStep));
    });

    return lines;
  }

  private parseStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    switch (step.stepTypeId) {
      case StepTypes.Dialogue:
        return this.parseDialogueStep(step, branch, isLastStep);
      case StepTypes.Thinking:
        return this.parseThinkingStep(step, branch, isLastStep);
      case StepTypes.Reaction:
        return this.parseReactionStep(step, branch, isLastStep);
      case StepTypes.Narrator:
        return this.parseNarratorStep(step, branch, isLastStep);
      case StepTypes.Choice:
        return this.parseChoiceStep(step, branch, isLastStep);
      case StepTypes.Check:
        return this.parseCheckStep(step, branch, isLastStep);
      case StepTypes.Remember:
        return this.parseRememberStep(step, branch, isLastStep);
      case StepTypes.Random:
        return this.parseRandomStep(step, branch, isLastStep);
      case StepTypes.Ending:
        return this.parseEndingStep(step);
      case StepTypes.Achieve:
        return this.parseAchieveStep(step, branch, isLastStep);
      case StepTypes.Texting:
        return this.parseTextingStep(step, branch, isLastStep);
      case StepTypes.Video:
        return this.parseVideoStep(step, branch, isLastStep);
      default:
        return [`\t# Unsupported step type: ${step.stepTypeId}`];
    }
  }

  private getCharacterAlias(step: BranchStep): string {
    if (step.speakerMemoryId != null) {
      const memoryName = this.dictionaries.variables.get(Number(step.speakerMemoryId));
      if (memoryName) return `${CHARACTER_MEMORY_PREFIX}${memoryName}`;
    }
    if (step.characterId == null) return 'Character_unknown';
    return this.dictionaries.characters.get(Number(step.characterId)) ?? 'Character_unknown';
  }

  private getExpressionCommand(step: BranchStep, commandKey?: EpisodeTextImportCommandKey): string {
    let command = commandKey ? `#${commandKey}` : '';

    if (step.characterExpressionId == null) return command.trim();

    const expressionValue = this.dictionaries.expressions.get(Number(step.characterExpressionId));
    if (!expressionValue) return command.trim();

    const isExpressionValid = Object.entries(EpisodeTextImportCommandKey)
      .some(([key, value]) => key.startsWith('Expression') && value === expressionValue);

    if (isExpressionValid) {
      const commandName = EXPRESSION_VALUE_TO_COMMAND[expressionValue] ?? expressionValue;
      command += ` #${commandName}`;
    }

    return command.trim();
  }

  private parseBranchGoto(branch: Branch): string | null {
    if (!branch.gotoBranchId) return null;
    const branchName = this.dictionaries.branchNames.get(Number(branch.gotoBranchId));
    if (branchName) {
      return `\t#${EpisodeTextImportCommandKey.Goto} ${branchName}`;
    }
    return `\t#${EpisodeTextImportCommandKey.Goto} [unknown:${branch.gotoBranchId}]`;
  }

  private appendBranchGotoIfLast(lines: string[], branch: Branch, isLastStep: boolean): void {
    if (isLastStep) {
      const goto = this.parseBranchGoto(branch);
      if (goto) lines.push(goto);
    }
  }

  /**
   * Returns the 4th-column tag for a step ID, including the leading tab separator.
   * NOTE: The leading \t is intentionally included here so callers can append it directly
   * to a 3-column TSV string. `importNodeToTsv` uses a different pattern (tab added externally)
   * to avoid importing BranchTextParser — both produce identical output.
   */
  private sidTag(step: BranchStep): string {
    return step.id != null ? `\t__sid:${step.id}` : '';
  }

  private resolveVariable(variableId: number | null | undefined): string {
    if (variableId == null) return '';
    return this.dictionaries.variables.get(Number(variableId)) ?? String(variableId);
  }

  private isCharacterMemory(variableId: number | string | null | undefined): boolean {
    if (variableId == null) return false;
    return this.dictionaries.variableTypes.get(Number(variableId)) === MemoryType.Character;
  }

  private resolveCheckValueAgainstCharacter(value: unknown): string {
    if (typeof value === 'object' && value !== null && 'type' in (value as Record<string, unknown>)) {
      const link = value as { type: string; variableId?: number | string };
      if (link.type === MemoryCheckValueType.Variable && link.variableId != null) {
        const otherName = this.dictionaries.variables.get(Number(link.variableId));
        if (otherName) return `${CHARACTER_MEMORY_PREFIX}${otherName}`;
      }
    }
    if (typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)))) {
      const alias = this.dictionaries.characters.get(Number(value));
      if (alias) return alias;
    }
    return String(resolveMemoryValue(value as MemoryValue, this.dictionaries.variables));
  }

  private resolveExpression(value: MemoryValue | unknown): string {
    return String(resolveMemoryValue(value, this.dictionaries.variables));
  }

  private resolveRememberValue(step: BranchStep): string {
    if (!step.action) return '';
    const variableType = this.dictionaries.variableTypes.get(Number(step.action.variableId));
    if (variableType !== MemoryType.Character || step.action.type !== BranchStepMemoryActionType.Set) {
      return this.resolveExpression(step.action.value);
    }

    const rawValue = step.action.value;
    if (typeof rawValue === 'object' && rawValue !== null && 'type' in (rawValue as Record<string, unknown>)) {
      const link = rawValue as { type: string; variableId?: number | string };
      if (link.type === MemoryCheckValueType.Variable && link.variableId != null) {
        const otherName = this.dictionaries.variables.get(Number(link.variableId));
        if (otherName) return `${CHARACTER_MEMORY_PREFIX}${otherName}`;
      }
    }

    const expressionString = this.resolveExpression(rawValue);
    const characterMemoryId = Number(expressionString);
    if (Number.isNaN(characterMemoryId)) {
      return expressionString;
    }
    return this.dictionaries.characters.get(characterMemoryId) ?? expressionString;
  }

  private parseDialogueStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const characterName = this.getCharacterAlias(step);
    const command = this.getExpressionCommand(step);
    lines.push(`${characterName}\t${step.text || ''}\t${command}${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseThinkingStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const characterName = this.getCharacterAlias(step);
    const command = this.getExpressionCommand(step, EpisodeTextImportCommandKey.StepThink);
    lines.push(`${characterName}\t${step.text || ''}\t${command}${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseReactionStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const characterName = this.getCharacterAlias(step);
    const command = this.getExpressionCommand(step, EpisodeTextImportCommandKey.StepReact);
    lines.push(`${characterName}\t${step.text || ''}\t${command}${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseNarratorStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    lines.push(`\t${step.text || ''}\t${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseChoiceStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const characterName = this.getCharacterAlias(step);
    const command = this.getExpressionCommand(step);
    lines.push(`${characterName}\t#${EpisodeTextImportCommandKey.StepChoice} ${step.text || ''}\t${command}${this.sidTag(step)}`);

    if (step.answers && step.answers.length > 0) {
      step.answers.forEach((answer) => {
        const answerText = `\t${answer.text}`;
        const answerGoto = answer.gotoBranchId
          ? `#${EpisodeTextImportCommandKey.Goto} ${this.dictionaries.branchNames.get(Number(answer.gotoBranchId)) ?? `[unknown:${answer.gotoBranchId}]`}`
          : '';
        const premiumCommand = answer.answerPrice ? ` #${EpisodeTextImportCommandKey.PremiumAnswer}: ${answer.answerPrice}` : '';

        let reqCommand = '';
        if (
          answer.requirement?.check?.operator
          && answer.requirement.check.variableId !== undefined
          && answer.requirement.check.value !== undefined
          && answer.requirement.check.value !== null
        ) {
          const req = answer.requirement;
          const operator = CheckOperator[req.check.operator];
          if (operator) {
            const isCharacterReq = this.isCharacterMemory(req.check.variableId);
            const variableName = this.resolveVariable(req.check.variableId);
            const value = isCharacterReq
              ? this.resolveCheckValueAgainstCharacter(req.check.value)
              : resolveMemoryValue(req.check.value, this.dictionaries.variables);
            reqCommand = ` #${EpisodeTextImportCommandKey.StepReq}: ${variableName} ${operator.short} ${value}`;
            reqCommand += `, otherwise ${req.visualTreatmentIfNotMet || 'Hide'}`;
            reqCommand += req.isBadgeHidden ? ', Hide indicator' : ', Show indicator';
          }
        }
        const aidTag = answer.id != null ? `\t__aid:${answer.id}` : '';
        lines.push(`${answerText}\t${answerGoto}${premiumCommand}${reqCommand}${aidTag}`);
      });
    }

    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseCheckStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];

    if (!step.check) return lines;

    const stepCheck = step.check;

    // Handle condition list checks
    if (stepCheck.operator === StepCheckOperatorType.ConditionList) {
      const variableName = this.resolveVariable(stepCheck.variableId);
      lines.push(`\t#${EpisodeTextImportCommandKey.StepCheck}: CONDITIONS (${variableName})\t${this.sidTag(step)}`);

      if (step.switch && step.switch.length > 0) {
        step.switch.forEach((switchCase) => {
          if (switchCase.operator) {
            const operator = CheckOperator[switchCase.operator as StepCheckOperatorType];
            let value: string;

            if (switchCase.variable !== undefined) {
              value = this.resolveVariable(switchCase.variable as number);
            } else if (switchCase.number !== undefined) {
              value = String(switchCase.number);
            } else {
              value = '';
            }

            const gotoBranch = switchCase.gotoBranchId
              ? this.dictionaries.branchNames.get(Number(switchCase.gotoBranchId)) ?? `[unknown:${switchCase.gotoBranchId}]`
              : '';
            lines.push(`\tif ${operator.short} ${value}\t#${EpisodeTextImportCommandKey.Goto} ${gotoBranch}`);
          } else {
            const gotoBranch = switchCase.gotoBranchId
              ? this.dictionaries.branchNames.get(Number(switchCase.gotoBranchId)) ?? `[unknown:${switchCase.gotoBranchId}]`
              : '';
            lines.push(`\telse\t#${EpisodeTextImportCommandKey.Goto} ${gotoBranch}`);
          }
        });
      }
      return lines;
    }

    // Handle PlayerHas checks
    if (stepCheck.operator === StepCheckOperatorType.PlayerHas) {
      const rewardIds = Array.isArray(stepCheck.value) ? stepCheck.value as number[] : [];
      const rewardNames = rewardIds.map(id => this.dictionaries.rewards.get(Number(id)) ?? String(id));
      const matchType = (stepCheck.match ?? 'all').toUpperCase();
      let checkLine = `\t#${EpisodeTextImportCommandKey.StepCheck}: PLAYERHAS ${matchType} (${rewardNames.join(', ')})`;
      if (step.switch && step.switch.length > 0) {
        const trueCase = step.switch.find((s) => s.value === true || s.value === 'true');
        const falseCase = step.switch.find((s) => s.value === false || s.value === 'false');
        const trueGotoId = Number(trueCase?.gotoBranchId);
        const falseGotoId = Number(falseCase?.gotoBranchId);
        const trueGoto = trueGotoId
          ? this.dictionaries.branchNames.get(trueGotoId) ?? `[unknown:${trueCase?.gotoBranchId}]`
          : '';
        const falseGoto = falseGotoId
          ? this.dictionaries.branchNames.get(falseGotoId) ?? `[unknown:${falseCase?.gotoBranchId}]`
          : '';
        checkLine += `, if Yes goto ${trueGoto}, if No goto ${falseGoto}`;
      }
      lines.push(`${checkLine}\t${this.sidTag(step)}`);
      this.appendBranchGotoIfLast(lines, branch, isLastStep);
      return lines;
    }

    // Handle Min/Max checks
    let checkLine = `\t#${EpisodeTextImportCommandKey.StepCheck}: `;

    if (stepCheck.operator === StepCheckOperatorType.Min || stepCheck.operator === StepCheckOperatorType.Max) {
      if (typeof stepCheck.value === 'object' && stepCheck.value !== null && 'variableId' in (stepCheck.value as Record<string, unknown>)) {
        const valObj = stepCheck.value as { type?: string; variableId?: number[] };
        if (Array.isArray(valObj.variableId)) {
          const varNames = valObj.variableId.map(id => this.resolveVariable(id));
          checkLine += `${stepCheck.operator.toUpperCase()} (${varNames.join(', ')})`;
        }
      } else if (typeof stepCheck.value === 'object' && stepCheck.value !== null && 'variable' in (stepCheck.value as Record<string, unknown>)) {
        const valObj = stepCheck.value as unknown as { variable: (string | number)[] };
        const varNames = Array.isArray(valObj.variable)
          ? valObj.variable.map(v => typeof v === 'number' ? this.resolveVariable(v) : String(v))
          : [];
        checkLine += `${stepCheck.operator.toUpperCase()} (${varNames.join(', ')})`;
      }

      if (step.switch && step.switch.length > 0) {
        step.switch.forEach((s) => {
          const switchValue = this.resolveVariable(Number(s.value));
          const gotoBranch = s.gotoBranchId
            ? this.dictionaries.branchNames.get(Number(s.gotoBranchId)) ?? `[unknown:${s.gotoBranchId}]`
            : '';
          checkLine += `, if ${switchValue} ${EpisodeTextImportCommandKey.Goto} ${gotoBranch}`;
        });
      }
    } else {
      // Handle single variable checks
      const operator = CheckOperator[stepCheck.operator];
      if (!operator) {
        lines.push(`\t# Unsupported check operator: ${stepCheck.operator}`);
        this.appendBranchGotoIfLast(lines, branch, isLastStep);
        return lines;
      }
      const isCharacterCheck = this.isCharacterMemory(stepCheck.variableId);
      const variableName = this.resolveVariable(stepCheck.variableId);
      const value = isCharacterCheck
        ? this.resolveCheckValueAgainstCharacter(stepCheck.value)
        : resolveMemoryValue(stepCheck.value, this.dictionaries.variables);
      checkLine += `if ${variableName} ${operator.short} ${value}`;
      if (step.switch && step.switch.length > 0) {
        // Backend may send value as boolean (true/false) or string ("true"/"false")
        const trueCase = step.switch.find((s) => s.value === true || s.value === 'true');
        const falseCase = step.switch.find((s) => s.value === false || s.value === 'false');
        const trueGoto = trueCase?.gotoBranchId
          ? this.dictionaries.branchNames.get(Number(trueCase.gotoBranchId)) ?? `[unknown:${trueCase.gotoBranchId}]`
          : '';
        const falseGoto = falseCase?.gotoBranchId
          ? this.dictionaries.branchNames.get(Number(falseCase.gotoBranchId)) ?? `[unknown:${falseCase.gotoBranchId}]`
          : '';
        checkLine += `, if Yes goto ${trueGoto}, if No goto ${falseGoto}`;
      }
    }
    lines.push(`${checkLine}\t${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseRememberStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];

    if (step.action) {
      const variableName = this.resolveVariable(step.action.variableId);

      switch (step.action.type) {
        case BranchStepMemoryActionType.Set: {
          const value = this.resolveRememberValue(step);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} = ${value}\t${this.sidTag(step)}`);
          break;
        }
        case BranchStepMemoryActionType.Increase: {
          const value = this.resolveExpression(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} + ${value}\t${this.sidTag(step)}`);
          break;
        }
        case BranchStepMemoryActionType.Decrease: {
          const value = this.resolveExpression(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} - ${value}\t${this.sidTag(step)}`);
          break;
        }
        case BranchStepMemoryActionType.View: {
          const viewValues = Array.isArray(step.action.value)
            ? step.action.value.join(', ')
            : String(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} view [${viewValues}]\t${this.sidTag(step)}`);
          break;
        }
        default:
          lines.push(`\t# Unsupported action type: ${(step.action as { type: string }).type}`);
          break;
      }
    }

    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseRandomStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];

    if (step.roll) {
      const variableName = this.resolveVariable(step.roll.variableId);

      switch (step.roll.type) {
        case BranchStepMemoryRollType.Dice: {
          const diceNotation = step.roll.diceNotation || '1d6';
          lines.push(`\t#random ${variableName} dice ${diceNotation}\t${this.sidTag(step)}`);
          break;
        }
        case BranchStepMemoryRollType.Set:
        case BranchStepMemoryRollType.Increase:
        case BranchStepMemoryRollType.Decrease: {
          const rollPrefix = step.roll.type === BranchStepMemoryRollType.Set ? '='
            : step.roll.type === BranchStepMemoryRollType.Increase ? '+'
              : '-';
          const minValue = this.resolveExpression(step.roll.min);
          const maxValue = this.resolveExpression(step.roll.max);
          const modifierValue = step.roll.modifier !== undefined && step.roll.modifier !== 0
            ? ` modifier ${step.roll.modifier}`
            : '';
          lines.push(`\t#random ${variableName} ${rollPrefix} [${minValue}, ${maxValue}]${modifierValue}\t${this.sidTag(step)}`);
          break;
        }
        default:
          lines.push(`\t# Unsupported roll type: ${(step.roll as { type: string }).type}`);
          break;
      }
    }

    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseEndingStep(step: BranchStep): string[] {
    return [`#${EpisodeTextImportCommandKey.StepEnding}\t${step.text || ''}\t${this.sidTag(step)}`];
  }

  private parseAchieveStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const achieveId = step.achievementId != null
      ? (this.dictionaries.achievements.get(Number(step.achievementId)) ?? String(step.achievementId))
      : '';
    lines.push(`\t#${EpisodeTextImportCommandKey.StepAchieve} ${achieveId}\t${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseTextingStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const characterName = this.getCharacterAlias(step);
    lines.push(`${characterName}\t${step.text || ''}\t#${EpisodeTextImportCommandKey.StepChat}${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }

  private parseVideoStep(step: BranchStep, branch: Branch, isLastStep: boolean): string[] {
    const lines: string[] = [];
    const videoTitle = step.video?.title || step.video?.url || '';
    const loop = step.video?.videoSettings?.loop ? 'yes' : 'no';
    const action = step.video?.videoSettings?.allowStepActions ? 'yes' : 'no';
    lines.push(`\t#${EpisodeTextImportCommandKey.StepVideo} ${videoTitle} loop=${loop} action=${action}\t${this.sidTag(step)}`);
    this.appendBranchGotoIfLast(lines, branch, isLastStep);
    return lines;
  }
}
